import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Modal,
    Steps,
    Form,
    Input,
    DatePicker,
    Select,
    InputNumber,
    Space,
    Typography,
    Alert,
    Checkbox,
    Divider,
    message
} from 'antd';
import {
    InfoCircleOutlined,
    HistoryOutlined,
    SettingOutlined,
    RocketOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { strategyApi } from '../../../api/client';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

interface NewPlanWizardProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (values: any) => void;
    isLoading?: boolean;
}

export default function NewPlanWizard({ visible, onCancel, onCreate, isLoading }: NewPlanWizardProps) {
    const [currentStep, setCurrentStep] = useState(0);
    const [form] = Form.useForm();

    // Reset step when modal opens
    React.useEffect(() => {
        if (visible) {
            setCurrentStep(0);
            form.resetFields();
        }
    }, [visible, form]);

    // Fetch active initiatives
    const { data: initiativesData } = useQuery({
        queryKey: ['active-initiatives'],
        queryFn: () => strategyApi.getInitiatives({}).then(res => res.data),
        enabled: visible
    });

    const initiatives = initiativesData || [];

    const steps = [
        { title: 'Podstawy', icon: <SettingOutlined /> },
        { title: 'Strategia', icon: <RocketOutlined /> }
    ];

    const next = async () => {
        try {
            const fieldsOnThisStep = currentStep === 0
                ? ['name', 'period']
                : [];

            await form.validateFields(fieldsOnThisStep);
            setCurrentStep(currentStep + 1);
        } catch (e) {
            console.warn('Validation failed on step', currentStep, e);
        }
    };

    const prev = () => {
        setCurrentStep(currentStep - 1);
    };

    const handleFinish = (values: any) => {
        const payload = {
            ...values,
            periodStart: values.period[0].startOf('month').toISOString(),
            periodEnd: values.period[1].startOf('month').toISOString(),
        };
        onCreate(payload);
    };

    const handleFinishFailed = (errorInfo: any) => {
        console.error('Final form validation failed:', errorInfo);
        message.warning('Proszę poprawić błędy w formularzu przed utworzeniem planu.');
    };

    return (
        <Modal
            title="Kreator Planu Sprzedaży"
            open={visible}
            onCancel={() => {
                form.resetFields();
                setCurrentStep(0);
                onCancel();
            }}
            onOk={() => {
                if (currentStep === steps.length - 1) {
                    form.submit();
                } else {
                    next();
                }
            }}
            okText={currentStep === steps.length - 1 ? 'Utwórz Plan' : 'Dalej'}
            cancelText={currentStep === 0 ? 'Anuluj' : 'Wstecz'}
            cancelButtonProps={{
                onClick: (e) => {
                    if (currentStep > 0) {
                        e.preventDefault();
                        e.stopPropagation();
                        prev();
                    } else {
                        onCancel();
                    }
                }
            }}
            confirmLoading={isLoading}
            width={600}
            destroyOnClose
        >
            <Steps size="small" current={currentStep} items={steps} style={{ marginBottom: 32 }} />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                onFinishFailed={handleFinishFailed}
                initialValues={{
                    periodType: 'MONTHLY',
                    autoSeedHistory: false,
                    bucketGranularity: 'MONTHLY'
                }}
            >
                <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
                    <Form.Item name="name" label="Nazwa Planu" rules={[{ required: true, message: 'Nazwa planu jest wymagana' }]}>
                        <Input placeholder="Np. Plan Strategiczny 2026" />
                    </Form.Item>

                    <Space size="large" align="start">
                        <Form.Item name="period" label="Okres Planowania" rules={[{ required: true, message: 'Wybierz zakres dat' }]}>
                            <RangePicker picker="month" />
                        </Form.Item>

                        <Form.Item name="bucketGranularity" label="Granularność">
                            <Select options={[
                                { label: 'Miesięczna', value: 'MONTHLY' },
                                { label: 'Tygodniowa', value: 'WEEKLY' }
                            ]} />
                        </Form.Item>
                    </Space>

                    <Form.Item name="autoSeedHistory" valuePropName="checked">
                        <Checkbox>Uzupełnij plan danymi historycznymi (Prognoza Bazowa)</Checkbox>
                    </Form.Item>

                    <Form.Item name="description" label="Opis / Cel biznesowy">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                </div>

                <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
                    <Alert
                        message="Linkowanie ze Strategią"
                        description="Możesz powiązać ten plan z konkretną inicjatywą OKR, aby śledzić realizację celów strategicznych."
                        type="info"
                        showIcon
                        icon={<RocketOutlined />}
                        style={{ marginBottom: 24 }}
                    />

                    <Form.Item name="initiativeId" label="Powiąż ze Strategiczną Inicjatywą">
                        <Select
                            placeholder="Wybierz inicjatywę OKR (opcjonalnie)"
                            allowClear
                            options={initiatives.map((i: any) => ({
                                label: `${i.name} (${i.status})`,
                                value: i.id
                            }))}
                        />
                    </Form.Item>

                    <Divider />
                    <Title level={5}>Podsumowanie</Title>
                    <ul style={{ paddingLeft: 20 }}>
                        <li><Text>Plan dla okresu: </Text><Text strong>{form.getFieldValue('period')?.[0] ? dayjs(form.getFieldValue('period')[0]).format('MMM YYYY') : '-'} - {form.getFieldValue('period')?.[1] ? dayjs(form.getFieldValue('period')[1]).format('MMM YYYY') : '-'}</Text></li>
                        <li><Text>Auto-Baseline: </Text><Text strong>{form.getFieldValue('autoSeedHistory') ? 'TAK' : 'NIE'}</Text></li>
                    </ul>
                </div>
            </Form>
        </Modal>
    );
}
